import React from 'react';
import { DynamicAssets, DynamicAssetsKeys, NavItem, ValuePropositionContent } from './models';
import { getPartnerResource } from '.';
import styled from 'styled-components';
import HeroTitle from './components/HeroTitle';
import HeroSubtitle from './components/HeroSubtitle';
import CalculatorHeader from './components/CalculatorHeader';
import { ProtectItCta } from './components';
import { DisclaimerLink } from '../components/Disclaimer';
import { formatDollars } from '../pages/calculator/utils';
import BREAK_POINTS from '../styling/media-queries';
import HeroImageContainer from './components/HeroImageContainer';
import ProtectItTitle from './components/ProtectItTitle';
import ProtectItContainer from './components/ProtectItContainer';
import ProtectItTextContainer from './components/ProtectItTextContainer';
import ValuePropTitle from './components/ValuePropTitle';
import CalculatorResultsHeader from './components/CalculatorResultsHeader';
import FinePrintTitle from './components/FinePrintTitle';
import TestimonialTitle from './components/TestimonialTitle';
import MoreInformationLink from './components/ValuePropositionLink';
import ValuePropositionImageContainer from './components/ValuePropositionImageContainer';
import EnrollmentTitle from './components/EnrollmentTitle';
import HeroContentSpacer from './components/HeroContentSpacer';
import SignupImageContainer from './components/SignupImageContainer';
import CompetitorPriceCell from './components/CalculatorCompetitorPriceCell';
import signupImage2 from './resources/signup-image-2.jpg';
import signupImage3 from './resources/signup-image-3.jpg';
import signupImage4 from './resources/signup-image-4.jpg';
import signupImage5 from './resources/signup-image-5.jpg';
import WhatsCoveredCallToActionTitle from './components/WhatsCoveredCallToActionTitle';
import ConfirmationCallButton from './components/ConfirmationCallButton';
import HeaderCallToAction from './components/HeaderCallToAction';
import { RESIDENCE_PAGE_NUMBER, REVIEW_PAGE_NUMBER, LinkId } from '../constants';
import { getTotalPurchaseAmount } from '../pages/signup/utils';
import { getPath } from '../pages/auth/utils';
import { AuthPath } from '../pages/auth/models';
import { openChat } from '../utils';

const EXPERT_SESSIONS_PATH = '/expert-sessions';
const PRODUCT_NAME = 'Smart Home Support';

const navLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.HeaderMenuExpertSessions,
    label: 'Expert sessions',
    to: EXPERT_SESSIONS_PATH,
  },
  {
    id: LinkId.HeaderMenuChat,
    label: 'Chat with an expert',
    onClick: () => openChat(),
  },
  {
    id: LinkId.HeaderMenuClaims,
    label: 'File a claim',
    to: '{{ claimUrl }}',
    target: '_blank',
  },
  {
    id: LinkId.HeaderMenuFaq,
    label: 'FAQ',
    to: '/faq',
  },
  {
    id: LinkId.HeaderMenuLogin,
    label: 'Log in',
    to: getPath(AuthPath.Login),
  },
];

const loggedInHeaderLinks: NavItem[] = [
  {
    id: LinkId.HeaderMenuWhatsCovered,
    label: 'What\u2019s covered?',
    to: '/whats-covered',
  },
  {
    id: LinkId.HeaderMenuFaq,
    label: 'FAQs',
    to: '/faq',
  },
  {
    id: LinkId.HeaderMenuExpertSessions,
    label: 'Expert sessions',
    to: EXPERT_SESSIONS_PATH,
  },
  {
    id: LinkId.HeaderMenuDashboard,
    label: 'Dashboard',
    to: '/dashboard',
  },
  {
    id: LinkId.HeaderMenuLogout,
    label: 'Log out',
    to: getPath(AuthPath.Logout),
  },
];

export const valuePropositionContent: ValuePropositionContent[] = [
  {
    icon: getPartnerResource('get-refund-icon.svg'),
    caption: 'Get money back',
    title: (
      <>
        See if you can get some <br /> money back from old <br /> tech warranties
      </>
    ),
    subtitle: (
      <>
        With Smart Home, you may
        <br /> not need your old tech warranties
        <br /> anymore.
      </>
    ),
    points: [],
    image: getPartnerResource('get-refund-image.jpg'),
    imageMargins: { desktop: { right: true, bottom: true, top: true }, mobile: {} },
    contentMargins: { desktop: { left: true, bottom: true, top: true }, mobile: {} },
    link: <MoreInformationLink light={true} text="Show me how" to="/dashboard/warranty-selection" />,
    shouldShowBanner: false,
    label: 'refund',
  },
  {
    icon: getPartnerResource('value-prop-icon-1.svg'),
    caption: 'Protection & repairs',
    title: <>Worry-free protection</>,
    subtitle: <>Because accidents can happen to anyone.</>,
    points: [
      'Coverage regardless of age',
      <>Can&rsquo;t repair? We&rsquo;ll try to replace</>,
      <>Can&rsquo;t replace? We&rsquo;ll send a check for market value</>,
    ],
    image: getPartnerResource('value_prop_img_1.jpg'),
    imageMargins: { desktop: { left: true, bottom: true, top: true }, mobile: {} },
    contentMargins: { desktop: { right: true, bottom: true, top: true }, mobile: {} },
    link: <MoreInformationLink light={false} text="See everything we cover" to="/whats-covered" />,
  },
  {
    icon: getPartnerResource('value-prop-icon-2.svg'),
    caption: 'Unlimited expert support',
    title: <>Experts you can talk to and understand</>,
    subtitle: <>No robots. No run-around.</>,
    points: [
      'U.S. based support',
      'Available 24/7, 365 days a year',
      'Millions of happy callers every month',
      'Our average rating is 4.8 out of 5 stars',
    ],
    image: getPartnerResource('value_prop_img_2.jpg'),
    imageMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { left: true, top: true, bottom: true }, mobile: {} },
    // link: <MoreInformationLink text="See how support works" />,
  },
  {
    icon: getPartnerResource('value-prop-icon-3.svg'),
    caption: 'No paperwork or receipts',
    title: <>All of the coverage</>,
    subtitle: <>None of the hassle.</>,
    points: [
      <>It doesn&rsquo;t matter where or when you bought your device</>,
      'No expiration dates to keep track of',
      'No receipts to hunt down',
    ],
    image: getPartnerResource('value_prop_img_3.jpg'),
    imageMargins: { desktop: { left: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    link: <MoreInformationLink light={false} text="See everything we cover" to="/whats-covered" />,
  },
  {
    icon: getPartnerResource('value-prop-icon-4.svg'),
    caption: 'Learn from the best',
    title: <>Expert guided learning</>,
    subtitle: 'Get smart about your home.',
    points: [
      'Subject matter experts, trained and ready to teach',
      '15 minute personalized conversation',
      'Schedule a time that works best',
      'Pick from our growing menu of topics, or suggest a new one',
    ],
    image: getPartnerResource('value_prop_img_4.jpg'),
    imageMargins: { desktop: { right: true, top: true, bottom: true }, mobile: {} },
    contentMargins: { desktop: { left: true, top: true, bottom: true }, mobile: { bottom: true } },
    link: <MoreInformationLink light text="Schedule a session" to={EXPERT_SESSIONS_PATH} />,
  },
];

const ProtectItSubtitle = (props: { maxPrice: number }) => {
  const { maxPrice } = props;
  return (
    <>
      {maxPrice > 0 ? (
        <>
          Individual warranties could cost you <strong>{formatDollars(Math.round(maxPrice * 24), 0)}</strong>
          <DisclaimerLink>2</DisclaimerLink> and they're a hassle to keep up with.
        </>
      ) : (
        <>Individual warranties can be limited and a hassle to keep up with.</>
      )}
      <> We've thrown out the old model to give you the service you deserve.</>
    </>
  );
};

const CalculatorLogoContainer = styled.div`
  box-sizing: border-box;
  padding: 0 5px;
  @media ${BREAK_POINTS.tablet} {
    padding: 0 50px;
  }
`;

const CalculatorLogoBase = styled.img`
  width: 100%;
  height: 100%;
`;

const CalculatorLogoNormal = styled(CalculatorLogoBase)`
  display: none;
  @media ${BREAK_POINTS.tablet} {
    display: inherit;
  }
`;

const CalculatorLogoSmall = styled(CalculatorLogoBase)`
  @media ${BREAK_POINTS.tablet} {
    display: none;
  }
`;

const CalculatorLogo = () => (
  <CalculatorLogoContainer>
    <CalculatorLogoNormal src={getPartnerResource('vzw-calc-logo.svg')} />
    <CalculatorLogoSmall src={getPartnerResource('vzw-calc-logo-small.svg')} />
  </CalculatorLogoContainer>
);

const SignupSubtitle = styled.div`
  margin-bottom: 4px;
  font-weight: 600;
  color: white;
  font-size: 16px;
  text-align: center;
  @media ${BREAK_POINTS.desktop} {
    font-size: 18px;
  }
`;

const ProductValueBackground = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 0;
  width: 100%;
  @media ${BREAK_POINTS.desktop} {
    width: auto;
  }
`;

const ProductValueTextContainer = styled.div`
  z-index: 0;
  padding: 12px;
  width: fill-available;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  @media ${BREAK_POINTS.desktop} {
    padding: 25px;
    max-width: initial;
  }
`;

const getPriceToDisplay = (signupStep: number, price: number, tax: number) =>
  signupStep <= RESIDENCE_PAGE_NUMBER ? price : getTotalPurchaseAmount(price, tax);

const PricingTextBold = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: white;
`;

export type ProductValueProps = {
  step: number;
  price: number;
  tax: number;
  subtitle: string;
};

const SignupPricingComponent: React.SFC<ProductValueProps> = (props) => {
  const shouldShowPrice = props.step > RESIDENCE_PAGE_NUMBER && props.step < REVIEW_PAGE_NUMBER;
  const pricingText = `$${getPriceToDisplay(props.step, props.price, props.tax)}/month`;

  return (
    <ProductValueBackground>
      <ProductValueTextContainer>
        <SignupSubtitle>{props.subtitle}</SignupSubtitle>
        {shouldShowPrice && <PricingTextBold>{pricingText}</PricingTextBold>}
      </ProductValueTextContainer>
    </ProductValueBackground>
  );
};

const HOME_CALL_TO_ACTION = 'Join now';

const assets: DynamicAssets = {
  [DynamicAssetsKeys.contactAgreementContainer]: () => null,
  [DynamicAssetsKeys.chatUrl]: undefined,
  [DynamicAssetsKeys.expertSessionsPath]: EXPERT_SESSIONS_PATH,
  [DynamicAssetsKeys.expertSessionsCallMeLaterSubtitle]: 'Your confirmation text is on the way!',
  [DynamicAssetsKeys.loggedInFooterNavLinks]: navLinks,
  [DynamicAssetsKeys.footerNavLinks]: navLinks,
  [DynamicAssetsKeys.loggedInHeaderNavLinks]: loggedInHeaderLinks,
  [DynamicAssetsKeys.headerNavLinks]: navLinks,
  [DynamicAssetsKeys.protectItImage]: require('./resources/protect_it.jpg'),
  [DynamicAssetsKeys.headerMobileCtaComponent]: (props: any) => (
    <HeaderCallToAction {...props}>{HOME_CALL_TO_ACTION}</HeaderCallToAction>
  ),
  [DynamicAssetsKeys.homeCallToAction]: HOME_CALL_TO_ACTION,

  [DynamicAssetsKeys.footerPartnerLogoImage]: null,
  [DynamicAssetsKeys.footerPartnerLogoHref]: null,

  [DynamicAssetsKeys.footerOnlineClaimButtonEnabled]: true,

  [DynamicAssetsKeys.confirmationPageDetailsLogo]: require('./resources/shs-logo-white.svg'),

  [DynamicAssetsKeys.productName]: PRODUCT_NAME,

  [DynamicAssetsKeys.finePrintIcon1]: getPartnerResource(DynamicAssetsKeys.finePrintIcon1),
  [DynamicAssetsKeys.finePrintIcon2]: getPartnerResource(DynamicAssetsKeys.finePrintIcon2),
  [DynamicAssetsKeys.finePrintIcon3]: getPartnerResource(DynamicAssetsKeys.finePrintIcon3),

  [DynamicAssetsKeys.homeHeroSpacerComponent]: HeroContentSpacer,
  [DynamicAssetsKeys.homeHeroTitle]: <>The last warranty you&rsquo;ll ever need.</>,
  [DynamicAssetsKeys.homeHeroTitleComponent]: HeroTitle,
  [DynamicAssetsKeys.homeHeroSubtitle]: <>Your home&rsquo;s connected devices. All under one plan.</>,
  [DynamicAssetsKeys.homeHeroSubtitleComponent]: HeroSubtitle,
  [DynamicAssetsKeys.homeHeroImage]: getPartnerResource(DynamicAssetsKeys.homeHeroImage),
  [DynamicAssetsKeys.homeHeroImageContainerComponent]: HeroImageContainer,

  [DynamicAssetsKeys.valueCalculatorHeading1]: (
    <>
      The average home has invested over {formatDollars(4000, 0)} <DisclaimerLink>1</DisclaimerLink> in home tech.
    </>
  ),
  [DynamicAssetsKeys.valueCalculatorHeading2]: 'How do you compare?',
  [DynamicAssetsKeys.valueCalculatorHeadingComponent]: CalculatorHeader,
  [DynamicAssetsKeys.valueCalculatorSubtitle]: 'Select all the devices in your home and see.',

  [DynamicAssetsKeys.homeProtectItTitleComponent]: ProtectItTitle,
  [DynamicAssetsKeys.homeProtectItTitle]: <>You invested, now protect it</>,
  [DynamicAssetsKeys.homeProtectItSubtitle]: (
    <>
      Individual warranties can be limited and a hassle to keep up with. We&rsquo;ve thrown out the old model to give
      you the service you deserve.
    </>
  ),
  [DynamicAssetsKeys.homeProtectItSubtitleComponent]: ProtectItSubtitle,
  [DynamicAssetsKeys.homeProtectItSubtitle2]: 'For just $20/month',
  [DynamicAssetsKeys.homeProtectItCtaComponent]: (props: any) => (
    <ProtectItCta {...props}>Join {PRODUCT_NAME}</ProtectItCta>
  ),
  [DynamicAssetsKeys.homeProtectItTextContainer]: ProtectItTextContainer,
  [DynamicAssetsKeys.homeProtectItContainer]: ProtectItContainer,

  [DynamicAssetsKeys.homeValuePropTitleComponent]: ValuePropTitle,
  [DynamicAssetsKeys.homeValuePropImageContainer]: ValuePropositionImageContainer,
  [DynamicAssetsKeys.homeValuePropContent]: valuePropositionContent,

  [DynamicAssetsKeys.calculatorResultsHeadingComponent]: CalculatorResultsHeader,
  [DynamicAssetsKeys.calculatorResultsHeading]: `${PRODUCT_NAME} is designed to give you more.`,
  [DynamicAssetsKeys.calculatorResultsSubheading]: 'For only $20/month',
  [DynamicAssetsKeys.calculatorResultsIndividualHeading]: 'Individual warranties',
  [DynamicAssetsKeys.calculatorResultsTableData]: (maxPrice: number) => [
    [<>The price?</>, <>Only $20/month</>, <CompetitorPriceCell maxPrice={maxPrice} />],
    [<>When can I buy the coverage?</>, <>Anytime</>, <>Typically within 30 days</>],
    [<>What if my TV is 3 years old?</>, <>It's covered</>, <>Probably out of luck</>],
    [<>What if I buy something new?</>, <>Still just $20/month</>, <>Another plan, another payment</>],
    [<>24/7 technical support?</>, <>Included</>, <>Up to $200 for 2 years</>],
    [<>Learning with experts?</>, <>Included</>, <>Up to $99/session</>],
    [<>Do I need to keep receipts?</>, <>No</>, <>Typically</>],
  ],
  [DynamicAssetsKeys.calculatorResultsLogoComponent]: CalculatorLogo,
  [DynamicAssetsKeys.calculatorDisclaimer2]: `Based on your answers from above. Individual warranties estimate based on
    total upfront costs for available protection plans.`,

  [DynamicAssetsKeys.finePrintTitleComponent]: FinePrintTitle,
  [DynamicAssetsKeys.finePrintTitle]: <>Let&rsquo;s magnify the fine print.</>,
  [DynamicAssetsKeys.finePrintSection1Title]: 'Is it really only $20/mo?',
  [DynamicAssetsKeys.finePrintSection1Detail]: (
    <>It&rsquo;s $20 a month, plus sales tax - determined by the state you live in. Cancel at anytime.</>
  ),
  [DynamicAssetsKeys.finePrintSection2Title]: 'Any service fees or deductibles?',
  [DynamicAssetsKeys.finePrintSection2Details]: (
    <>Some devices have a $49 or $89 service fee which includes parts, labor and replacement if needed.</>
  ),
  [DynamicAssetsKeys.finePrintSection3Title]: 'Is there a claim limit?',
  [DynamicAssetsKeys.finePrintSection3Details]: (
    <>There is a $2,000 per claim limit, with a $5,000 aggregate limit per year.</>
  ),
  [DynamicAssetsKeys.finePrintLinkEnabled]: true,
  [DynamicAssetsKeys.finePrintLinkText]: 'See terms and conditions for complete details',
  [DynamicAssetsKeys.finePrintLinkTo]: '/terms-conditions',
  [DynamicAssetsKeys.finePrintLinkLabel]: 'Terms & Conditions',

  [DynamicAssetsKeys.testimonialTitleComponent]: TestimonialTitle,
  [DynamicAssetsKeys.testimonialTitle]: 'Sounds too good to be true?',
  [DynamicAssetsKeys.testimonialSubtitle]: 'Yeah, we hear that a lot.',
  [DynamicAssetsKeys.testimonialBody]: (
    <>
      <b>{PRODUCT_NAME}</b> by Asurion extends Asurion&rsquo;s tried-and-true device protection and support services to
      cover the tech in your home.
      <br />
      <br />
      For more than 20 years, Asurion has helped millions of customers recover from cracked screens, liquid damage, and
      device malfunctions with a 4.8 average rating.
    </>
  ),

  [DynamicAssetsKeys.signupSubtitleComponent]: SignupSubtitle,
  [DynamicAssetsKeys.signupTitleComponent]: EnrollmentTitle,
  [DynamicAssetsKeys.signupImageComponent]: SignupImageContainer,
  [DynamicAssetsKeys.signupPricingComponent]: SignupPricingComponent,

  [DynamicAssetsKeys.signupValueProp1]: 'Your home tech, covered.',
  [DynamicAssetsKeys.signupValueProp2]: 'Expert support available 24/7.',
  [DynamicAssetsKeys.signupValueProp3]: 'Protection for the old and the new.',

  [DynamicAssetsKeys.signupImage1]: getPartnerResource('home-hero-cover.jpg'),
  [DynamicAssetsKeys.signupImage2]: signupImage2,
  [DynamicAssetsKeys.signupImage3]: signupImage3,
  [DynamicAssetsKeys.signupImage4]: signupImage4,
  [DynamicAssetsKeys.signupImage5]: signupImage5,

  [DynamicAssetsKeys.contactSubmitButtonId]: 'contact-submit-shs',
  [DynamicAssetsKeys.residenceSubmitButtonId]: 'residence-submit-shs',
  [DynamicAssetsKeys.billingSubmitButtonId]: 'billing-submit-shs',
  [DynamicAssetsKeys.reviewSubmitButtonId]: 'enroll-submit-shs',
  [DynamicAssetsKeys.confirmationCallButtonId]: 'confirmation-call-button-id-shs',
  [DynamicAssetsKeys.confirmationChatButtonId]: 'confirmation-chat-button-id-shs',
  [DynamicAssetsKeys.confirmationExpertSessionsButtonId]: 'confirmation-expert-sessions-button-id-shs',

  // Signup confirmation page
  [DynamicAssetsKeys.confirmationWelcomeMessage]: `It’s official! You’re covered. You now have protection and unlimited
    support for your smart home.`,
  [DynamicAssetsKeys.confirmationChatHeading]: `Your team of experts is ready for you.`,
  [DynamicAssetsKeys.confirmationChatMessage]: `Get real time help now! Your ${PRODUCT_NAME} team is ready to tackle
    any issue; from setting up new devices to trouble shooting and getting the most from existing tech.`,
  [DynamicAssetsKeys.confirmationCallMessage]: `Talk to a real person—no waiting, no robots.`,
  [DynamicAssetsKeys.confirmationCallHeading]: 'Rather call?',
  [DynamicAssetsKeys.confirmationCallComponent]: ConfirmationCallButton,

  [DynamicAssetsKeys.whatsCoveredCallToActionTitle]: 'All your tech, one simple plan.',
  [DynamicAssetsKeys.whatsCoveredCallToActionComponent]: WhatsCoveredCallToActionTitle,
  [DynamicAssetsKeys.whatsCoveredCallToActionInverted]: true,

  [DynamicAssetsKeys.expertChatAppKey]: 'd80f85cc-9cd8-463f-87a8-5f88f35d2ce4',
  [DynamicAssetsKeys.expertChatSdkEnabled]: true,
};

export default assets;



// WEBPACK FOOTER //
// ./packages/smart-home-support/assets/SHS-WEB.tsx